import { useState, useEffect, useId } from "react";
import { TextField, InputAdornment, CircularProgress, IconButton, Alert } from "@mui/material";
import { Lock, Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import PersonIcon from "@mui/icons-material/Person";
import { Box, Typography } from "@mui/material";
import { useAuth } from "../components/AuthContext/AuthContext";
import { useSip } from "../components/SipContext";
import logoCredi from "../assets/img//logo-credipoint.webp"
import axios from "../configApi/axiosConfig";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
} from "@mui/material";
import {
	TimerOff as TimerOffIcon,
	Security as SecurityIcon,
	CheckCircle as CheckCircleIcon,
	Cancel as CancelIcon,
} from "@mui/icons-material";
import { APIURL } from "../configApi/apiConfig";

/* ============================================================
   COLORES ECUADOR
   Rojo:   #EB2A1B
   Azul:   #2D3689
   ============================================================ */

/* ============================================================
   MASCOTA POINT (SVG)
   Poses: "peek" (saluda), "juggle" (dominadas), "kick" (patea),
   "keeper" (arquero), "run" (corre), "redcard" (saca tarjeta roja)
   cubreOjos: se tapa los ojos con los guantes (al mostrar la clave).
   ============================================================ */
const PointMascot = ({ pose = "peek", size = 120, flip = false, cubreOjos = false }) => {
	const uid = useId().replace(/[:]/g, "");
	const clipId = `pclip-${uid}`;

	const remaches = [48, 62, 76, 90, 104, 118, 132, 146].map((x) => {
		const t = (x - 48) / 98;
		return { x, y: 52 - Math.sin(t * Math.PI) * 14 };
	});

	return (
		<div
			className={`mascota pose-${pose}`}
			style={{
				width: size,
				height: size * 1.3,
				transform: flip ? "scaleX(-1)" : "none",
				position: "relative",
				display: "inline-block",
			}}
		>
			<svg viewBox="0 0 200 260" width="100%" height="100%" style={{ overflow: "visible" }}>
				<defs>
					<clipPath id={clipId}>
						<circle cx="100" cy="105" r="78" />
					</clipPath>
				</defs>

				{/* piernas */}
				<g className="pierna pierna-i">
					<path d="M84 172 Q80 200 78 214" stroke="#EB2A1B" strokeWidth="10" fill="none" strokeLinecap="round" />
					<rect x="64" y="208" width="28" height="9" rx="4.5" fill="#e8eaf0" />
					<path d="M60 216 h32 a8 8 0 0 1 8 8 v6 a8 8 0 0 1 -8 8 h-34 a10 10 0 0 1 -10 -10 c0 -7 5 -12 12 -12 z" fill="#2D3689" />
					<rect x="66" y="222" width="22" height="3" rx="1.5" fill="#e8eaf0" />
					<rect x="66" y="228" width="22" height="3" rx="1.5" fill="#e8eaf0" />
				</g>
				<g className="pierna pierna-d">
					<path d="M116 172 Q120 200 122 214" stroke="#EB2A1B" strokeWidth="10" fill="none" strokeLinecap="round" />
					<rect x="108" y="208" width="28" height="9" rx="4.5" fill="#e8eaf0" />
					<path d="M108 216 h32 a10 10 0 0 1 10 10 c0 7 -5 12 -12 12 h-32 a8 8 0 0 1 -8 -8 v-6 a8 8 0 0 1 8 -8 z" fill="#2D3689" />
					<rect x="112" y="222" width="22" height="3" rx="1.5" fill="#e8eaf0" />
					<rect x="112" y="228" width="22" height="3" rx="1.5" fill="#e8eaf0" />
				</g>

				{/* brazos (se ocultan si cubreOjos) */}
				{!cubreOjos && pose !== "redcard" && (
					<>
						<g className="brazo brazo-i">
							<path d="M36 112 Q12 100 10 72" stroke="#EB2A1B" strokeWidth="9" fill="none" strokeLinecap="round" />
							<rect x="0" y="62" width="22" height="8" rx="4" fill="#e8eaf0" transform="rotate(-14 11 66)" />
							<circle cx="8" cy="48" r="13" fill="#2D3689" />
							<circle cx="-1" cy="36" r="5.5" fill="#2D3689" />
							<circle cx="14" cy="33" r="5" fill="#2D3689" />
						</g>
						<g className="brazo brazo-d">
							<path d="M164 112 Q188 100 190 72" stroke="#EB2A1B" strokeWidth="9" fill="none" strokeLinecap="round" />
							<rect x="178" y="62" width="22" height="8" rx="4" fill="#e8eaf0" transform="rotate(14 189 66)" />
							<circle cx="192" cy="48" r="13" fill="#2D3689" />
							<circle cx="201" cy="36" r="5.5" fill="#2D3689" />
							<circle cx="186" cy="33" r="5" fill="#2D3689" />
						</g>
					</>
				)}

				{/* pose redcard: brazo derecho levantado con tarjeta roja */}
				{pose === "redcard" && !cubreOjos && (
					<>
						{/* brazo izquierdo colgado */}
						<g className="brazo brazo-i">
							<path d="M36 112 Q24 130 22 148" stroke="#EB2A1B" strokeWidth="9" fill="none" strokeLinecap="round" />
							<circle cx="19" cy="160" r="13" fill="#2D3689" />
						</g>
						{/* brazo derecho levantado con tarjeta */}
						<g className="brazo brazo-d redcard-arm">
							<path d="M164 112 Q186 90 192 60" stroke="#EB2A1B" strokeWidth="9" fill="none" strokeLinecap="round" />
							<circle cx="192" cy="48" r="13" fill="#2D3689" />
							{/* tarjeta roja */}
							<rect x="196" y="18" width="22" height="30" rx="3" fill="#EB2A1B" stroke="#c0180a" strokeWidth="1.5" />
						</g>
					</>
				)}

				{/* cuerpo */}
				<g className="cuerpo">
					<circle cx="100" cy="105" r="78" fill="#2D3689" />
					<rect x="20" y="156" width="160" height="40" fill="#EB2A1B" clipPath={`url(#${clipId})`} />
					<ellipse cx="66" cy="52" rx="26" ry="12" fill="#ffffff" opacity="0.22" transform="rotate(-22 66 52)" />
					<path d="M44 54 Q100 26 156 54" stroke="#1a255e" strokeWidth="3" fill="none" />
					{remaches.map((r, i) => (
						<circle key={i} cx={r.x} cy={r.y} r="2.6" fill="#d9dde6" />
					))}
					<rect x="34" y="58" width="132" height="76" rx="38" fill="#EB2A1B" stroke="#d6dae2" strokeWidth="7" />
					<ellipse cx="74" cy="94" rx="8.5" ry="11" fill="#0c0c0c" />
					<circle cx="77" cy="89" r="2.6" fill="#ffffff" />
					<ellipse cx="126" cy="94" rx="8.5" ry="11" fill="#0c0c0c" />
					<circle cx="129" cy="89" r="2.6" fill="#ffffff" />
					<g fill="#3fd9ff">
						{[
							[72, 6], [78, 11], [84, 7], [90, 15], [96, 10],
							[102, 16], [108, 9], [114, 13], [120, 6], [126, 10],
						].map(([x, h], i) => (
							<rect key={i} x={x} y={150 - h / 2} width="3" height={h} rx="1.5" />
						))}
					</g>

					{/* guantes tapando los ojos */}
					{cubreOjos && (
						<g>
							<path d="M36 112 Q28 72 56 86" stroke="#EB2A1B" strokeWidth="9" fill="none" strokeLinecap="round" />
							<path d="M164 112 Q172 72 144 86" stroke="#EB2A1B" strokeWidth="9" fill="none" strokeLinecap="round" />
							<circle cx="74" cy="93" r="16" fill="#2D3689" stroke="#1a255e" strokeWidth="2" />
							<circle cx="64" cy="82" r="5.5" fill="#2D3689" />
							<circle cx="74" cy="78" r="5.5" fill="#2D3689" />
							<circle cx="84" cy="81" r="5.5" fill="#2D3689" />
							<circle cx="126" cy="93" r="16" fill="#2D3689" stroke="#1a255e" strokeWidth="2" />
							<circle cx="116" cy="81" r="5.5" fill="#2D3689" />
							<circle cx="126" cy="78" r="5.5" fill="#2D3689" />
							<circle cx="136" cy="82" r="5.5" fill="#2D3689" />
						</g>
					)}
				</g>
			</svg>
		</div>
	);
};

/* Mascota KO con X en los ojos — usada en la escena de choque */
const PointMascotKO = ({ size = 86, flip = false }) => {
	const uid = useId().replace(/[:]/g, "");
	const clipId = `pclip-ko-${uid}`;
	return (
		<div
			style={{
				width: size,
				height: size * 1.3,
				transform: flip ? "scaleX(-1)" : "none",
				display: "inline-block",
				position: "relative",
			}}
		>
			<svg viewBox="0 0 200 260" width="100%" height="100%" style={{ overflow: "visible" }}>
				<defs>
					<clipPath id={clipId}>
						<circle cx="100" cy="105" r="78" />
					</clipPath>
				</defs>
				{/* piernas extendidas al frente (caído) */}
				<g>
					<path d="M84 172 Q76 195 60 208" stroke="#EB2A1B" strokeWidth="10" fill="none" strokeLinecap="round" />
					<path d="M50 204 h32 a8 8 0 0 1 8 8 v6 a8 8 0 0 1 -8 8 h-34 a10 10 0 0 1 -10 -10 c0 -7 5 -12 12 -12 z" fill="#2D3689" />
				</g>
				<g>
					<path d="M116 172 Q124 195 140 208" stroke="#EB2A1B" strokeWidth="10" fill="none" strokeLinecap="round" />
					<path d="M130 204 h32 a10 10 0 0 1 10 10 c0 7 -5 12 -12 12 h-32 a8 8 0 0 1 -8 -8 v-6 a8 8 0 0 1 8 -8 z" fill="#2D3689" />
				</g>
				{/* brazos abiertos (caído) */}
				<g>
					<path d="M36 112 Q10 118 -10 128" stroke="#EB2A1B" strokeWidth="9" fill="none" strokeLinecap="round" />
					<circle cx="-14" cy="132" r="13" fill="#2D3689" />
				</g>
				<g>
					<path d="M164 112 Q190 118 210 128" stroke="#EB2A1B" strokeWidth="9" fill="none" strokeLinecap="round" />
					<circle cx="214" cy="132" r="13" fill="#2D3689" />
				</g>
				{/* cuerpo */}
				<circle cx="100" cy="105" r="78" fill="#2D3689" />
				<rect x="20" y="156" width="160" height="40" fill="#EB2A1B" clipPath={`url(#${clipId})`} />
				<rect x="34" y="58" width="132" height="76" rx="38" fill="#EB2A1B" stroke="#d6dae2" strokeWidth="7" />
				{/* Ojos X */}
				<text x="64" y="102" fontSize="22" fontWeight="900" fill="#1a1a1a" textAnchor="middle" fontFamily="Arial">✕</text>
				<text x="126" y="102" fontSize="22" fontWeight="900" fill="#1a1a1a" textAnchor="middle" fontFamily="Arial">✕</text>
				{/* estrellas de mareo */}
				<g>
					<circle cx="100" cy="42" r="5" fill="#FFD700" />
					<circle cx="118" cy="30" r="4" fill="#FFD700" />
					<circle cx="82" cy="30" r="4" fill="#FFD700" />
					<circle cx="100" cy="20" r="6" fill="#EB2A1B" />
				</g>
			</svg>
		</div>
	);
};


const Balon = ({ size = 30 }) => (
	<svg viewBox="0 0 40 40" width={size} height={size}>
		{/* Base blanca con borde negro */}
		<circle cx="20" cy="20" r="18.5" fill="#ffffff" stroke="#1a1a1a" strokeWidth="1.4" />
		{/* Pentágono central amarillo Ecuador */}
		<polygon points="20,12 27,17 24.4,25 15.6,25 13,17" fill="#FFD700" stroke="#1a1a1a" strokeWidth="0.8" />
		{/* Parches triangulares alternando rojo y azul Ecuador */}
		<polygon points="20,12 20,3 26,7" fill="#EB2A1B" stroke="#1a1a1a" strokeWidth="0.6" />
		<polygon points="27,17 36,14 33,20" fill="#2D3689" stroke="#1a1a1a" strokeWidth="0.6" />
		<polygon points="24.4,25 30,32 23,34" fill="#EB2A1B" stroke="#1a1a1a" strokeWidth="0.6" />
		<polygon points="15.6,25 10,32 17,34" fill="#2D3689" stroke="#1a1a1a" strokeWidth="0.6" />
		<polygon points="13,17 4,14 7,20" fill="#FFD700" stroke="#1a1a1a" strokeWidth="0.6" />
		{/* Costuras */}
		<path d="M20 12 L20 3 M27 17 L36 14 M24.4 25 L30 32 M15.6 25 L10 32 M13 17 L4 14" stroke="#1a1a1a" strokeWidth="1.2" fill="none" />
	</svg>
);

/* Torre de iluminación del estadio */
const TorreLuz = () => (
	<div className="torre-luz">
		<div className="panel-focos">
			{[...Array(12)].map((_, i) => (
				<div key={i} className="foco" style={{ animationDelay: `${(i % 4) * 0.6}s` }} />
			))}
		</div>
		<div className="poste-luz" />
	</div>
);

const LoginMundial = () => {
	const { login, isLoggedIn, isSessionExpired2 } = useAuth();
	const { connect, disconnect } = useSip();
	const [sessionExpired, setShowExpiredModal] = useState(false);
	const [userName, setUserName] = useState("");
	const [password, setPassword] = useState("");
	const [messageError, setMessageError] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const navigate = useNavigate();
	const { enqueueSnackbar } = useSnackbar();
	const [mostrarCambioClave, setMostrarCambioClave] = useState(false);
	const [nuevaClave, setNuevaClave] = useState("");
	const [confirmarClave, setConfirmarClave] = useState("");
	const [showNewPassword, setShowNewPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);
	const [usuarioExtensionId, setUsuarioExtensionId] = useState(null);

	const [showOlvidoModal, setShowOlvidoModal] = useState(false);
	const [usuarioOlvido, setUsuarioOlvido] = useState("");
	const [cedulaOlvido, setCedulaOlvido] = useState("");
	const [isLoadingOlvido, setIsLoadingOlvido] = useState(false);

	// Estado para la tarjeta roja en el mascot del login
	const [mascotRedCard, setMascotRedCard] = useState(false);

	const handleOlvidoPassword = async () => {
		if (!usuarioOlvido.trim() || !cedulaOlvido.trim()) {
			enqueueSnackbar("Por favor complete todos los campos", { variant: "error" });
			return;
		}

		setIsLoadingOlvido(true);
		try {
			const response = await axios.post(APIURL.recuperarClave(), {
				nombreUsuario: usuarioOlvido,
				cedula: cedulaOlvido,
			});

			const mensaje = response.data;

			if (mensaje === 'La contraseña ha sido enviada al correo registrado') {
				enqueueSnackbar(mensaje, { variant: "success" });
				setShowOlvidoModal(false);
				setUsuarioOlvido("");
				setCedulaOlvido("");
			} else {
				enqueueSnackbar(mensaje, { variant: "error" });
			}
		} catch (error) {
			console.error("Error al recuperar contraseña:", error);

			let mensajeError = "Error al procesar la solicitud. Intente nuevamente.";

			if (error.response?.data) {
				mensajeError = error.response.data;
			} else if (error.response?.status === 400) {
				mensajeError = "Datos inválidos. Verifique su usuario y cédula.";
			} else if (error.response?.status === 404) {
				mensajeError = "Usuario no encontrado o datos incorrectos.";
			} else if (error.response?.status >= 500) {
				mensajeError = "Error del servidor. Intente más tarde.";
			}

			enqueueSnackbar(mensajeError, { variant: "error" });
		} finally {
			setIsLoadingOlvido(false);
		}
	};

	const validarContrasena = (password) => {
		const minLength = password.length >= 8 && password.length <= 12;
		const hasUpperCase = /[A-Z]/.test(password);
		const hasLowerCase = /[a-z]/.test(password);
		const hasSpecialChar = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password);

		return {
			minLength,
			hasUpperCase,
			hasLowerCase,
			hasSpecialChar,
			isValid: minLength && hasUpperCase && hasLowerCase && hasSpecialChar
		};
	};

	const passwordValidation = validarContrasena(nuevaClave);

	useEffect(() => {
		if (isSessionExpired2) {
			setShowExpiredModal(true);
		}
	}, [isSessionExpired2]);

	const handleCloseModal = () => {
		setShowExpiredModal(false);
	};

	useEffect(() => {
		if (isLoggedIn && !mostrarCambioClave) {
			navigate("/ciudadanos", { replace: true });
		}
	}, [isLoggedIn, mostrarCambioClave, navigate]);

	const conectarExtensionUsuario = async (idUsuario) => {
		disconnect();

		if (!idUsuario) return;

		try {
			const extensionResponse = await axios.get(APIURL.get_usuario_extension(idUsuario));
			const extensionData = Array.isArray(extensionResponse.data)
				? extensionResponse.data[0]
				: extensionResponse.data;
			const sipExtension = extensionData?.extension;
			const sipPassword = extensionData?.password_sip;

			if (sipExtension && sipPassword) {
				await connect({
					username: sipExtension,
					extension: sipExtension,
					password: sipPassword,
				});
			}
		} catch (sipError) {
			console.error("Error al consultar o conectar extension SIP:", sipError);
			disconnect();
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setIsLoading(true);
		setMessageError("");
		setMascotRedCard(false);

		try {
			const response = await axios.post("auth/login", {
				Nombre: userName,
				Clave: password,
			});

			if (response.status === 201) {
				const data = response.data;
				localStorage.setItem("token", data.token);
				setUsuarioExtensionId(data.idUsuario);

				const verificacion = await axios.get(APIURL.verificarCambioClave(userName));
				const debeCambiar = !verificacion.data.existe;

				if (debeCambiar) {
					disconnect();
					setMostrarCambioClave(true);
				} else {
					const expirationTime = new Date().getTime() + 3 * 60 * 60 * 1000;
					login(data.token, expirationTime);
					await conectarExtensionUsuario(data.idUsuario);
					enqueueSnackbar("Acceso correcto!", { variant: "success" });
					navigate("/dashboard", { replace: true });
				}

			}
		} catch (error) {
			console.error("Error al iniciar sesión:", error);
			setMessageError("Credenciales inválidas");
			// Activar tarjeta roja en el mascot
			setMascotRedCard(true);
			setTimeout(() => setMascotRedCard(false), 3000);
		} finally {
			setIsLoading(false);
		}
	};

	const obtenerIP = async () => {
		try {
			const response = await fetch("https://api.ipify.org?format=json");
			const data = await response.json();
			return data.ip;
		} catch (e) {
			console.error("Error al obtener IP:", e);
			return "127.0.0.1";
		}
	};

	return (
		<section
			className="flex items-center justify-center min-h-screen relative overflow-hidden"
			style={{ background: 'linear-gradient(180deg, #040d18 0%, #0a0e2e 52%, #0d1035 100%)' }}
		>
			{/* ====== Animaciones y estilos del estadio ====== */}
			<style>{`
        /* --- partes articuladas de la mascota --- */
        .mascota svg .pierna,
        .mascota svg .brazo { transform-box: fill-box; }
        .mascota svg .pierna-i { transform-origin: 60% 0%; }
        .mascota svg .pierna-d { transform-origin: 40% 0%; }
        .mascota svg .brazo-i  { transform-origin: 95% 95%; }
        .mascota svg .brazo-d  { transform-origin: 5% 95%; }

        /* --- estadio: torres de luz --- */
        .torre-luz { position: absolute; top: 3%; width: 110px; text-align: center; z-index: 1; }
        .panel-focos {
          display: grid; grid-template-columns: repeat(4, 1fr); gap: 5px;
          background: #222b38; border: 3px solid #46546a; border-radius: 8px;
          padding: 7px; width: 96px; margin: 0 auto;
        }
        .foco {
          width: 14px; height: 14px; border-radius: 50%; background: #fff7cf;
          box-shadow: 0 0 10px 3px rgba(255,240,170,.9);
          animation: parpadeoFoco 4s ease-in-out infinite;
        }
        @keyframes parpadeoFoco {
          0%, 100% { opacity: 1; }
          50%      { opacity: .75; }
        }
        .poste-luz { width: 7px; height: 110px; background: linear-gradient(90deg, #5a6678, #2c3442); margin: 0 auto; }
        .haz-luz {
          position: absolute; top: 60px; width: 420px; height: 70vh;
          background: linear-gradient(rgba(255,246,200,.16), transparent 72%);
          filter: blur(3px); pointer-events: none;
        }

        /* --- público que alienta en ola --- */
        @keyframes hinchada {
          0%, 100% {
            background-position: 0 6px, 8px 0, 16px 6px, 24px 1px;
          }
          25% {
            background-position: 8px 0, 16px 6px, 24px 1px, 32px 5px;
          }
          50% {
            background-position: 16px 6px, 24px 1px, 32px 5px, 40px 0;
          }
          75% {
            background-position: 24px 1px, 32px 5px, 40px 0, 48px 6px;
          }
        }
        .publico { animation: hinchada 2.8s ease-in-out infinite; }

        /* --- marcador LED --- */
        @keyframes brilloMarcador {
          0%, 100% { box-shadow: 0 0 22px rgba(255,215,0,.30), inset 0 0 18px rgba(0,0,0,.9); }
          50%      { box-shadow: 0 0 34px rgba(255,215,0,.55), inset 0 0 18px rgba(0,0,0,.9); }
        }
        .marcador-led { animation: brilloMarcador 3s ease-in-out infinite; }
        /* El mascot conserva el saltito detras de la card */
        @keyframes mascotGolpe {
          0%, 65%  { transform: translateY(0px); }
          75%      { transform: translateY(5px); }
          82%      { transform: translateY(-3px); }
          100%     { transform: translateY(0px); }
        }
        .mascot-card-wrap {
          animation: mascotGolpe 1.3s ease-in-out infinite;
        }

        /* ====== POSE: PEEK ====== */
        @keyframes peekBob {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-7px); }
        }
        @keyframes saludoI {
          0%, 100% { transform: rotate(0deg); }
          50%      { transform: rotate(16deg); }
        }
        @keyframes saludoD {
          0%, 100% { transform: rotate(0deg); }
          50%      { transform: rotate(-16deg); }
        }
        .pose-peek { animation: peekBob 2.6s ease-in-out infinite; }
        .pose-peek svg .brazo-i { animation: saludoI 2.6s ease-in-out infinite; }
        .pose-peek svg .brazo-d { animation: saludoD 2.6s ease-in-out infinite; }

        /* ====== POSE: TARJETA ROJA (mascot login) ====== */
        @keyframes redcardEntrada {
          0%   { transform: translateY(10px) scale(0.85); opacity: 0; }
          30%  { transform: translateY(-6px) scale(1.08); opacity: 1; }
          100% { transform: translateY(0) scale(1); opacity: 1; }
        }
        @keyframes redcardArmUp {
          0%   { transform: rotate(0deg); }
          40%  { transform: rotate(-30deg); }
          100% { transform: rotate(-30deg); }
        }
        .pose-redcard { animation: redcardEntrada 0.5s ease-out forwards; }
        .pose-redcard svg .redcard-arm { animation: redcardArmUp 0.5s ease-out forwards; }

        /* ====== POSE: DOMINADAS ====== */
        @keyframes dominadaHop {
          0%, 100% { transform: translateY(0); }
          8%       { transform: translateY(-8px); }
          55%      { transform: translateY(0); }
        }
        @keyframes brazoBalanceI {
          0%, 100% { transform: rotate(0deg); }
          50%      { transform: rotate(14deg); }
        }
        @keyframes brazoBalanceD {
          0%, 100% { transform: rotate(0deg); }
          50%      { transform: rotate(-14deg); }
        }
        .pose-juggle { animation: dominadaHop 1.1s ease-in-out infinite; }
        .pose-juggle svg .brazo-i { animation: brazoBalanceI 1.1s ease-in-out infinite; }
        .pose-juggle svg .brazo-d { animation: brazoBalanceD 1.1s ease-in-out infinite; }
        /* ====== ESCENA DE PARTIDO: persecucion y gol (segunda mitad del ciclo) ====== */
        @keyframes partidoArco {
          0%, 48% { transform: translateX(calc(100vw + 220px)); opacity: 0; }
          54%     { transform: translateX(calc(68vw - 20px)); opacity: 1; }
          84%     { transform: translateX(calc(68vw - 20px)); opacity: 1; }
          86%     { transform: translateX(calc(68vw - 14px)); opacity: 1; }
          88%     { transform: translateX(calc(68vw - 25px)); opacity: 1; }
          90%     { transform: translateX(calc(68vw - 20px)); opacity: 1; }
          96%, 100% { transform: translateX(calc(100vw + 220px)); opacity: 0; }
        }
        @keyframes partidoDelantero {
          0%, 48% { transform: translateX(-220px); opacity: 0; }
          54%     { transform: translateX(-160px); opacity: 1; }
          68%     { transform: translateX(calc(40vw - 70px)); opacity: 1; }
          74%     { transform: translateX(calc(44vw - 70px)) rotate(-6deg); opacity: 1; }
          78%     { transform: translateX(calc(46vw - 70px)) rotate(8deg); opacity: 1; }
          92%     { transform: translateX(calc(58vw - 70px)); opacity: 1; }
          100%    { transform: translateX(calc(100vw + 180px)); opacity: 0; }
        }
        @keyframes partidoCompanero {
          0%, 50% { transform: translateX(-260px); opacity: 0; }
          56%     { transform: translateX(-210px); opacity: 1; }
          74%     { transform: translateX(calc(35vw - 70px)); opacity: 1; }
          92%     { transform: translateX(calc(48vw - 70px)); opacity: 1; }
          100%    { transform: translateX(calc(100vw + 120px)); opacity: 0; }
        }
        @keyframes partidoDefensa {
          0%, 50% { transform: translateX(calc(100vw + 180px)) scaleX(-1); opacity: 0; }
          57%     { transform: translateX(calc(100vw + 120px)) scaleX(-1); opacity: 1; }
          76%     { transform: translateX(calc(49vw + 30px)) scaleX(-1); opacity: 1; }
          86%     { transform: translateX(calc(45vw + 30px)) scaleX(-1); opacity: 1; }
          96%     { transform: translateX(calc(45vw + 30px)) scaleX(-1); opacity: 1; }
          100%    { transform: translateX(calc(45vw + 30px)) scaleX(-1); opacity: 0; }
        }
        @keyframes partidoBalon {
          0%, 50% { transform: translateX(-110px) translateY(0) rotate(0deg); opacity: 0; }
          54%     { transform: translateX(-90px) translateY(0) rotate(60deg); opacity: 1; }
          68%     { transform: translateX(calc(40vw + 10px)) translateY(-8px) rotate(420deg); opacity: 1; }
          75%     { transform: translateX(calc(47vw + 4px)) translateY(-3px) rotate(620deg); opacity: 1; }
          82%     { transform: translateX(calc(65vw + 15px)) translateY(-58px) rotate(980deg); opacity: 1; }
          86%     { transform: translateX(calc(70vw + 38px)) translateY(-46px) rotate(1120deg); opacity: 1; }
          89%     { transform: translateX(calc(70vw + 38px)) translateY(-46px) rotate(1120deg); opacity: 0; }
          100%    { transform: translateX(calc(100vw + 120px)) translateY(0) rotate(0deg); opacity: 0; }
        }
        @keyframes partidoArquero {
          0%, 70% { transform: translate(0, 0) rotate(0deg); }
          78%     { transform: translate(-8px, 2px) rotate(-4deg); }
          84%     { transform: translate(-42px, -28px) rotate(-34deg); }
          91%     { transform: translate(-46px, -22px) rotate(-38deg); }
          98%, 100% { transform: translate(0, 0) rotate(0deg); }
        }
        @keyframes partidoGritaGol {
          0%, 82% { opacity: 0; transform: scale(.35) rotate(-8deg); }
          86%     { opacity: 1; transform: scale(1.35) rotate(5deg); }
          91%     { opacity: 1; transform: scale(1) rotate(-2deg); }
          96%, 100% { opacity: 0; transform: scale(.65) rotate(0deg); }
        }
        @keyframes lagrimaDefensa {
          0%, 82% { opacity: 0; transform: scaleX(-1) translateY(-6px) scale(.4); }
          86%     { opacity: 1; transform: scaleX(-1) translateY(0) scale(1); }
          94%     { opacity: 1; transform: scaleX(-1) translateY(8px) scale(1.08); }
          100%    { opacity: 0; transform: scaleX(-1) translateY(12px) scale(.8); }
        }
        @keyframes defensaTrote {
          0%, 50%, 86%, 100% { transform: translateY(0); }
          58%, 66%, 74%, 82% { transform: translateY(-5px); }
        }
        @keyframes defensaPiernaI {
          0%, 50%, 86%, 100% { transform: rotate(0deg); }
          58%, 74% { transform: rotate(26deg); }
          66%, 82% { transform: rotate(-26deg); }
        }
        @keyframes defensaPiernaD {
          0%, 50%, 86%, 100% { transform: rotate(0deg); }
          58%, 74% { transform: rotate(-26deg); }
          66%, 82% { transform: rotate(26deg); }
        }
        @keyframes defensaBrazoI {
          0%, 50%, 86%, 100% { transform: rotate(0deg); }
          58%, 74% { transform: rotate(-16deg); }
          66%, 82% { transform: rotate(16deg); }
        }
        @keyframes defensaBrazoD {
          0%, 50%, 86%, 100% { transform: rotate(0deg); }
          58%, 74% { transform: rotate(16deg); }
          66%, 82% { transform: rotate(-16deg); }
        }
        .partido-arco { animation: partidoArco 18s ease-in-out infinite; }
        .partido-delantero { animation: partidoDelantero 18s ease-in-out infinite; transform-origin: 50% 90%; }
        .partido-companero { animation: partidoCompanero 18s ease-in-out infinite; }
        .partido-defensa { animation: partidoDefensa 18s ease-in-out infinite; }
        .partido-defensa .pose-run { animation: defensaTrote 18s ease-in-out infinite; }
        .partido-defensa .pose-run svg .pierna-i { animation: defensaPiernaI 18s ease-in-out infinite; }
        .partido-defensa .pose-run svg .pierna-d { animation: defensaPiernaD 18s ease-in-out infinite; }
        .partido-defensa .pose-run svg .brazo-i { animation: defensaBrazoI 18s ease-in-out infinite; }
        .partido-defensa .pose-run svg .brazo-d { animation: defensaBrazoD 18s ease-in-out infinite; }
        .partido-lagrima {
          position: absolute;
          left: 64px;
          top: 44px;
          width: 8px;
          height: 13px;
          border-radius: 50% 50% 55% 55%;
          background: #55d7ff;
          box-shadow: 0 0 6px rgba(85,215,255,.9);
          animation: lagrimaDefensa 18s ease-in-out infinite;
        }
        .partido-balon { animation: partidoBalon 18s ease-in-out infinite; }
        .partido-arquero { animation: partidoArquero 18s ease-in-out infinite; transform-origin: 50% 80%; }
        .partido-gol-texto { animation: partidoGritaGol 18s ease-in-out infinite; }

        .pose-keeper svg .brazo-i { transform: rotate(26deg); }
        .pose-keeper svg .brazo-d { transform: rotate(-26deg); }

        /* ====== POSE: CORRE ====== */
        @keyframes corredorCruza {
          0%   { transform: translateX(-280px); }
          100% { transform: translateX(calc(100vw + 280px)); }
        }
        @keyframes corredorCruzaRev {
          0%   { transform: translateX(calc(100vw + 280px)); }
          100% { transform: translateX(-280px); }
        }
        @keyframes piernaCorreI { 0% { transform: rotate(26deg); } 100% { transform: rotate(-26deg); } }
        @keyframes piernaCorreD { 0% { transform: rotate(-26deg); } 100% { transform: rotate(26deg); } }
        @keyframes brazoCorreI  { 0% { transform: rotate(-16deg); } 100% { transform: rotate(16deg); } }
        @keyframes brazoCorreD  { 0% { transform: rotate(16deg); } 100% { transform: rotate(-16deg); } }
        @keyframes trotecito    { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-5px); } }
        @keyframes balonDribleado {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50%      { transform: translateY(-13px) rotate(180deg); }
        }

        /* ====== ANIMACIÓN DE CHOQUE DEFINITIVO ====== */
        /* UNA SOLA VEZ (iteration: 1, fill-mode: forwards):
           0s-3s   → corren hacia el centro
           3s      → choque + explosión
           3s-5s   → quedan tirados KO
           5s-8s   → árbitro sale, saca tarjeta roja, se va
           8s+     → todo desaparece, queda en silencio hasta el siguiente ciclo
        */
        @keyframes corredorIzq {
          0%   { transform: translateX(-200px); opacity: 1; }
          15.6% { transform: translateX(calc(50vw - 120px)); opacity: 1; }
          /* impacto */
          17.8% { transform: translateX(calc(50vw - 100px)) rotate(-85deg) translateY(28px); opacity: 1; }
          /* tirado KO — quieto absoluto */
          30.6% { transform: translateX(calc(50vw - 100px)) rotate(-85deg) translateY(28px); opacity: 1; }
          /* fundido a negro */
          37.8% { transform: translateX(calc(50vw - 100px)) rotate(-85deg) translateY(28px); opacity: 0; }
          100% { transform: translateX(calc(50vw - 100px)) rotate(-85deg) translateY(28px); opacity: 0; }
        }
        @keyframes corredorDer {
          0%   { transform: translateX(calc(100vw + 200px)) scaleX(-1); opacity: 1; }
          15.6% { transform: translateX(calc(50vw + 20px)) scaleX(-1); opacity: 1; }
          /* impacto */
          17.8% { transform: translateX(calc(50vw + 5px)) scaleX(-1) rotate(85deg) translateY(28px); opacity: 1; }
          /* tirado KO — quieto absoluto */
          30.6% { transform: translateX(calc(50vw + 5px)) scaleX(-1) rotate(85deg) translateY(28px); opacity: 1; }
          /* fundido */
          37.8% { transform: translateX(calc(50vw + 5px)) scaleX(-1) rotate(85deg) translateY(28px); opacity: 0; }
          100% { transform: translateX(calc(50vw + 5px)) scaleX(-1) rotate(85deg) translateY(28px); opacity: 0; }
        }
        @keyframes arbitroSale {
          0%, 18.3% { transform: translateX(calc(50vw - 230px)) translateY(-24px) scale(0); opacity: 0; }
          21.1%     { transform: translateX(calc(50vw - 230px)) translateY(-24px) scale(1); opacity: 1; }
          23.9%     { transform: translateX(calc(50vw - 230px)) translateY(-24px) scale(.88); opacity: 1; }
          33.3%     { transform: translateX(calc(50vw - 230px)) translateY(-24px) scale(.88); opacity: 1; }
          38.9%     { transform: translateX(calc(50vw - 230px)) translateY(-24px) scale(.1); opacity: 0; }
          100%      { transform: translateX(calc(50vw - 230px)) translateY(-24px) scale(0); opacity: 0; }
        }
        @keyframes estrellaChoque {
          0%, 14.4% { opacity: 0; transform: translateX(calc(50vw - 80px)) scale(0); }
          16.7%     { opacity: 1; transform: translateX(calc(50vw - 80px)) scale(2); }
          19.4%     { opacity: 1; transform: translateX(calc(50vw - 80px)) scale(1.2); }
          23.3%     { opacity: 0; transform: translateX(calc(50vw - 80px)) scale(0); }
          100%     { opacity: 0; transform: translateX(calc(50vw - 80px)) scale(0); }
        }

        /* Corriendo vs KO: swap al 31% y KO se queda hasta desaparecer */
        @keyframes mostrarCorriendo {
          0%, 16.7%   { opacity: 1; }
          17.2%, 100% { opacity: 0; }
        }
        @keyframes mostrarKO {
          0%, 16.7%   { opacity: 0; }
          17.2%, 30%  { opacity: 1; }
          37.8%, 100% { opacity: 0; }
        }

        .corredor-pista-izq {
          position: absolute; bottom: 2px;
          z-index: 2;
          animation: corredorIzq 18s ease-in-out infinite;
          will-change: transform;
        }
        .corredor-pista-der {
          position: absolute; bottom: 2px;
          z-index: 2;
          animation: corredorDer 18s ease-in-out infinite;
          will-change: transform;
        }
        .arbitro-pista {
          position: absolute; bottom: 26px;
          z-index: 1;
          animation: arbitroSale 18s ease-in-out infinite;
          will-change: transform;
        }
        .estrella-choque {
          position: absolute; bottom: 55px;
          z-index: 3;
          font-size: 2rem; pointer-events: none;
          animation: estrellaChoque 18s ease-in-out infinite;
        }
        .mascota-corriendo-izq { animation: mostrarCorriendo 18s ease-in-out infinite; }
        .mascota-corriendo-der { animation: mostrarCorriendo 18s ease-in-out infinite; }
        .mascota-ko-izq { animation: mostrarKO 18s ease-in-out infinite; position: absolute; left: 0; bottom: 0; }
        .mascota-ko-der { animation: mostrarKO 18s ease-in-out infinite; position: absolute; left: 0; bottom: 0; }

        .pose-run { animation: trotecito .36s ease-in-out infinite; }
        .pose-run svg .pierna-i { animation: piernaCorreI .36s ease-in-out infinite alternate; }
        .pose-run svg .pierna-d { animation: piernaCorreD .36s ease-in-out infinite alternate; }
        .pose-run svg .brazo-i  { animation: brazoCorreI .36s ease-in-out infinite alternate; }
        .pose-run svg .brazo-d  { animation: brazoCorreD .36s ease-in-out infinite alternate; }
        .balon-drible { animation: balonDribleado .5s ease-in-out infinite; }

        /* ====== BOTÓN LOGIN — animación sutil Ecuador ====== */
        @keyframes ecuadorShimmer {
          0%   { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        .btn-ecuador {
          background: linear-gradient(135deg, #EB2A1B 0%, #2D3689 50%, #EB2A1B 100%);
          background-size: 200% auto;
          transition: background-position 0.4s ease, box-shadow 0.3s ease;
        }
        .btn-ecuador:hover:not(:disabled) {
          background-position: right center;
          box-shadow: 0 4px 18px rgba(235,42,27,0.45), 0 2px 8px rgba(45,54,137,0.35);
        }

        /* accesibilidad */
        @media (prefers-reduced-motion: reduce) {
          .mascota, .mascota svg *, .balon-drible,
          .corredor-pista-izq, .corredor-pista-der, .arbitro-pista, .estrella-choque,
          .mascota-corriendo-izq, .mascota-corriendo-der, .mascota-ko-izq, .mascota-ko-der,
          .partido-arco, .partido-delantero, .partido-companero, .partido-defensa,
          .partido-balon, .partido-arquero, .partido-gol-texto, .partido-lagrima, .foco, .publico, .marcador-led {
            animation: none !important;
          }
        }
      `}</style>

			{/* ====== Techo del estadio ====== */}
			<div
				className="absolute pointer-events-none"
				style={{
					left: '-12%', width: '124%', top: 'calc(17% - 10px)', height: 14,
					borderRadius: '50% 50% 0 0 / 100% 100% 0 0',
					background: 'linear-gradient(180deg, #cfd6e0, #7c8794)',
					boxShadow: '0 3px 10px rgba(0,0,0,.5)',
				}}
			/>

			{/* ====== Gradas con público ====== */}
			<div
				className="absolute overflow-hidden pointer-events-none"
				style={{
					left: '-12%', width: '124%', top: '17%', height: '32%',
					borderRadius: '50% 50% 0 0 / 90% 90% 0 0',
					backgroundColor: '#0d1322',
					boxShadow: '0 -4px 30px rgba(0,0,0,.6), inset 0 14px 24px rgba(255,255,255,.05)',
				}}
			>
				{/* hinchada con colores Ecuador: rojo, azul, amarillo */}
				<div
					className="publico absolute inset-0"
					style={{
						backgroundImage: `
              radial-gradient(circle at 3px 4px, rgba(235,42,27,.85) 1.7px, transparent 2.4px),
              radial-gradient(circle at 11px 8px, rgba(255,215,0,.75) 1.7px, transparent 2.4px),
              radial-gradient(circle at 19px 4px, rgba(45,54,137,.9) 1.7px, transparent 2.4px),
              radial-gradient(circle at 26px 9px, rgba(235,235,235,.5) 1.5px, transparent 2.2px)`,
						backgroundSize: '30px 13px',
					}}
				/>
				{/* separación de tribunas */}
				<div
					className="absolute inset-0"
					style={{ background: 'repeating-linear-gradient(180deg, transparent 0 24px, rgba(0,0,0,.6) 24px 29px)' }}
				/>
			</div>

			{/* ====== Torres de iluminación con haces de luz ====== */}
			<div className="hidden md:block">
				<div style={{ position: 'absolute', left: 30, top: 0 }}><TorreLuz /></div>
				<div className="haz-luz" style={{ left: 40, clipPath: 'polygon(12% 0, 28% 0, 95% 100%, 45% 100%)' }} />
				<div style={{ position: 'absolute', right: 30, top: 0 }}><TorreLuz /></div>
				<div className="haz-luz" style={{ right: 40, clipPath: 'polygon(72% 0, 88% 0, 55% 100%, 5% 100%)' }} />
			</div>

			{/* ====== Marcador LED ====== */}
			<div className="absolute top-2.5 left-1/2 -translate-x-1/2 pointer-events-none z-[3]">
				<div
					className="marcador-led px-6 py-2 rounded-xl text-center"
					style={{ background: '#090d14', border: '3px solid #FFD700' }}
				>
					<div
						className="font-extrabold tracking-[4px] text-[15px] md:text-[17px]"
						style={{ color: '#FFD700', textShadow: '0 0 8px rgba(255,215,0,.8)' }}
					>
						⚽ COPA MUNDIAL 2026 ⚽
					</div>
				</div>
			</div>

			{/* ====== Cancha ====== */}
			<div
				className="absolute bottom-0 left-0 right-0 pointer-events-none"
				style={{
					height: '46%',
					background: 'repeating-linear-gradient(180deg, #1e7e22 0 34px, #2a962e 34px 68px)',
					boxShadow: 'inset 0 18px 30px rgba(0,0,0,.45)',
				}}
			>
				{/* línea de banda */}
				<div className="absolute rounded" style={{ top: 10, left: '3%', right: '3%', height: 4, background: 'rgba(255,255,255,.85)' }} />
				{/* círculo central */}
				<div
					className="absolute left-1/2 -translate-x-1/2"
					style={{
						bottom: '6%', width: 'min(520px, 70vw)', height: 190,
						border: '4px solid rgba(255,255,255,.7)', borderRadius: '50%',
					}}
				/>
				<div
					className="absolute left-1/2 -translate-x-1/2 rounded-full"
					style={{ bottom: 'calc(6% + 90px)', width: 12, height: 8, background: 'rgba(255,255,255,.8)' }}
				/>
			</div>

			{/* ====== Corredor izquierda -> derecha con choque mejorado ====== */}
			<div className="absolute bottom-0 left-0 w-full overflow-hidden pointer-events-none z-[2]" style={{ height: 190 }}>
				{/* Jugador 1: izquierda → derecha — corre luego queda KO */}
				<div className="corredor-pista-izq" style={{ position: 'absolute', bottom: 2, display: 'inline-flex', alignItems: 'flex-end', gap: 12 }}>
					<div className="mascota-corriendo-izq">
						<PointMascot pose="run" size={86} />
					</div>
					<div className="mascota-ko-izq">
						<PointMascotKO size={86} />
					</div>
					<div className="balon-drible" style={{ marginBottom: 4 }}><Balon size={27} /></div>
				</div>

				{/* Jugador 2: derecha → izquierda — corre luego queda KO */}
				<div className="corredor-pista-der" style={{ position: 'absolute', bottom: 2, display: 'inline-flex', alignItems: 'flex-end', gap: 12 }}>
					<div className="mascota-corriendo-der">
						<PointMascot pose="run" size={86} />
					</div>
					<div className="mascota-ko-der">
						<PointMascotKO size={86} flip />
					</div>
				</div>

				{/* Árbitro: sale al choque y saca tarjeta roja */}
				<div className="arbitro-pista inline-flex items-end">
					<PointMascot pose="redcard" size={96} />
				</div>

				{/* Estrella de choque */}
				<div className="estrella-choque">💥</div>

				{/* Partido: tres personajes corren tras el balón y uno define */}
				<div className="partido-arco absolute" style={{ bottom: 12, width: 175, height: 128 }}>
					<div
						style={{
							position: 'absolute',
							left: 20,
							bottom: 0,
							width: 150,
							height: 108,
							borderTop: '6px solid #f2f2f2',
							borderLeft: '6px solid #f2f2f2',
							borderRight: '6px solid #f2f2f2',
							borderRadius: '5px 5px 0 0',
							background: `
                repeating-linear-gradient(0deg, rgba(255,255,255,.28) 0 1px, transparent 1px 12px),
                repeating-linear-gradient(90deg, rgba(255,255,255,.28) 0 1px, transparent 1px 12px)`,
						}}
					/>
					<div className="partido-arquero absolute" style={{ left: 48, bottom: 0 }}>
						<PointMascot pose="keeper" size={72} />
					</div>
				</div>

				<div className="partido-companero absolute" style={{ bottom: 4 }}>
					<PointMascot pose="run" size={76} />
				</div>
				<div className="partido-delantero absolute" style={{ bottom: 4 }}>
					<PointMascot pose="run" size={82} />
				</div>
				<div className="partido-defensa absolute" style={{ bottom: 4 }}>
					<PointMascot pose="run" size={78} />
					<div className="partido-lagrima" />
				</div>
				<div className="partido-balon absolute" style={{ bottom: 28 }}>
					<Balon size={27} />
				</div>
				<div
					className="partido-gol-texto absolute font-extrabold"
					style={{
						bottom: 124,
						left: '66vw',
						color: '#FFD700',
						fontSize: '2rem',
						fontStyle: 'italic',
						textShadow: '0 0 12px rgba(255,215,0,.9), 2px 2px 0 #EB2A1B',
						whiteSpace: 'nowrap',
					}}
				>
					¡GOOOL!
				</div>

			</div>

			{/* ====== Point asomado detrás del card — dominadas con balón en cabeza / tarjeta roja si error ====== */}
			<div
				className="absolute left-1/2 top-1/2 pointer-events-none z-[5]"
				style={{ transform: 'translate(-50%, calc(-50% - 168px))' }}
			>
				<div className={!mascotRedCard && !showPassword ? "mascot-card-wrap" : ""}>
					<PointMascot
						pose={mascotRedCard ? "redcard" : (showPassword ? "peek" : "juggle")}
						size={148}
						cubreOjos={showPassword && !mascotRedCard}
					/>
				</div>
				{mascotRedCard && (
					<div
						style={{
							position: 'absolute',
							top: -10,
							right: -14,
							background: '#EB2A1B',
							color: '#fff',
							fontSize: '0.62rem',
							fontWeight: 'bold',
							padding: '2px 6px',
							borderRadius: 4,
							letterSpacing: 1,
							boxShadow: '0 2px 8px rgba(235,42,27,0.6)',
							animation: 'redcardEntrada 0.5s ease-out',
						}}
					>
						¡TARJETA ROJA!
					</div>
				)}
			</div>

			{/* ====== Login Card ====== */}
			<div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10" style={{ width: 280 }}>
				<div
					className="bg-white rounded-2xl relative overflow-hidden"
					style={{
						padding: '18px 20px 20px',
						boxShadow: '0 0 30px rgba(235,42,27,0.25), 0 0 60px rgba(45,54,137,0.2)',
						border: '2px solid rgba(45,54,137,0.3)',
					}}
				>
					{/* Barra superior Ecuador (sin animación) */}
					<div className="absolute top-0 left-0 right-0 h-2" style={{
						background: 'linear-gradient(90deg, #FFD700 0 50%, #2D3689 50% 75%, #EB2A1B 75% 100%)',
					}} />

					{/* Título */}
					<div className="text-center mb-4 relative">
						<div className="absolute -top-2 left-4 text-lg animate-bounce">⚽</div>
						<div className="absolute -top-2 right-4 text-lg animate-bounce" style={{ animationDelay: '0.2s' }}>🇪🇨</div>

						<img
							src={logoCredi}
							alt="Credi Point Digital"
							className="mx-auto mb-2"
							style={{ width: 120, height: 30, objectFit: 'contain' }}
						/>
						<h2
							className="font-bold"
							style={{ color: '#2D3689', fontSize: '0.95rem' }}
						>
							¡Ecuador al Mundial 2026!
						</h2>
						
					</div>

					{/* Formulario */}
					<div className="space-y-3">
						<form
							onSubmit={handleSubmit}
							autoComplete="on"
							className="space-y-4"
						>
							{/* Campo Usuario */}
							<div className="relative">
								<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
									<PersonIcon className="h-4 w-4 text-black-400" />
								</div>
								<input
									type="text"
									required
									value={userName}
									onChange={(e) => setUserName(e.target.value.toUpperCase())}
									placeholder="Usuario"
									className="w-full pl-10 pr-3 border border-gray-300 rounded-xl bg-gray-50 focus:outline-none text-gray-700"
									style={{ fontSize: '0.82rem', padding: '8px 12px 8px 36px' }}
								/>
							</div>

							{/* Campo Contraseña */}
							<div className="relative">
								<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
									<Lock className="h-4 w-4 text-black-400" />
								</div>
								<input
									type={showPassword ? "text" : "password"}
									placeholder="Contraseña"
									required
									value={password}
									onChange={(e) => setPassword(e.target.value)}
									className="w-full border border-gray-300 rounded-xl bg-gray-50 focus:outline-none text-gray-700"
									style={{ fontSize: '0.82rem', padding: '8px 60px 8px 36px' }}
								/>
								<button
									type="button"
									onClick={() => setShowPassword(!showPassword)}
									className="absolute inset-y-0 right-0 pr-4 flex items-center"
								>
									{showPassword ? (
										<span className="flex items-center space-x-2 font-semibold transition-colors duration-200" style={{ color: '#2D3689' }}>
											<span>Ocultar</span>
										</span>
									) : (
										<span className="flex items-center space-x-2 font-semibold transition-colors duration-200" style={{ color: '#2D3689' }}>
											<span>Mostrar</span>
										</span>
									)}
								</button>
							</div>

							{/* olvidaste contraseña */}
							<div className="flex items-center" style={{ fontSize: '0.75rem' }}>
								<button
									type="button"
									onClick={() => setShowOlvidoModal(true)}
									className="ml-auto hover:opacity-80 transition-opacity duration-200 whitespace-nowrap"
									style={{ color: '#EB2A1B' }}
								>
									¿Olvidaste tu contraseña?
								</button>

								{messageError && <div className="text-red-500 text-start text-xs">¡{messageError}!</div>}
							</div>

							{/* Botón Iniciar Sesión — colores Ecuador, animación sutil */}
							<button
								type="submit"
								disabled={isLoading}
								className="btn-ecuador w-full text-white rounded-xl font-semibold focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-300 relative overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed"
								style={{ fontSize: '0.85rem', padding: '9px 16px', marginTop: 8 }}
							>
								<span className="relative z-10 flex items-center justify-center gap-2">
									{isLoading ? (
										<>
											<CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
											Ingresando...
										</>
									) : (
										<>⚽ Iniciar Sesión</>
									)}
								</span>
							</button>
						</form>
					</div>
				</div>
			</div>

			{/* ====== Modal sesión expirada ====== */}
			<Dialog
				open={sessionExpired}
				onClose={handleCloseModal}
				PaperProps={{
					sx: {
						position: "absolute",
						top: "50%",
						left: "50%",
						transform: "translate(-50%, -50%)",
						width: 400,
						bgcolor: "background.paper",
						borderRadius: "8px",
						boxShadow: 24,
						p: 4,
						textAlign: "center",
					},
				}}
			>
				<DialogTitle>
					<Typography variant="h6" component="div">
						Sesión expirada <TimerOffIcon />
					</Typography>
				</DialogTitle>
				<DialogContent>
					<Typography sx={{ mt: 2 }}>
						Tu sesión ha expirado por inactividad. Por favor inicia sesión nuevamente.
					</Typography>
				</DialogContent>
				<DialogActions sx={{ justifyContent: "center", mt: 2 }}>
					<Button
						onClick={handleCloseModal}
						variant="contained"
						sx={{
							backgroundColor: "#2D3689",
							color: "#ffffff",
							"&:hover": {
								backgroundColor: "#1e2460",
							},
						}}
					>
						Aceptar
					</Button>
				</DialogActions>
			</Dialog>

			{/* ====== Modal de olvido de contraseña ====== */}
			<Dialog
				open={showOlvidoModal}
				onClose={() => setShowOlvidoModal(false)}
				maxWidth="sm"
				fullWidth
				PaperProps={{
					sx: {
						borderRadius: "12px",
						background: "linear-gradient(135deg, #EB2A1B 0%, #2D3689 100%)",
						color: "white",
					},
				}}
			>
				<DialogTitle sx={{
					textAlign: "center",
					pb: 1,
					background: "rgba(255, 255, 255, 0.96)",
					backdropFilter: "blur(6px)",
				}}>
					<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
						<Lock sx={{ fontSize: "2rem", color: "#2D3689" }} />
						<Typography variant="h5" component="div" sx={{ fontWeight: "bold", color: "#2D3689" }}>
							Recuperar Contraseña
						</Typography>
					</Box>
					<Typography variant="body2" sx={{ mt: 1, opacity: 0.9, color: "#444" }}>
						Ingrese sus datos para recuperar el acceso a su cuenta
					</Typography>
				</DialogTitle>

				<DialogContent sx={{
					p: 3,
					background: "rgba(255, 255, 255, 0.95)",
					color: "#333",
					m: 2,
					borderRadius: "8px",
				}}>
					<Alert
						severity="info"
						sx={{
							mb: 3,
							backgroundColor: "rgba(45,54,137, 0.08)",
							border: "1px solid rgba(45,54,137, 0.3)",
						}}
					>
						<Typography variant="body2" sx={{ fontWeight: "500" }}>
							🔐 Complete los siguientes datos para verificar su identidad
						</Typography>
					</Alert>

					<TextField
						label="Usuario"
						fullWidth
						margin="dense"
						value={usuarioOlvido}
						onChange={(e) => setUsuarioOlvido(e.target.value.toUpperCase())}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<PersonIcon sx={{ color: '#2D3689' }} />
								</InputAdornment>
							),
						}}
						sx={{
							"& .MuiOutlinedInput-root": { borderRadius: "8px" },
							mb: 2
						}}
					/>

					<TextField
						label="Número de Cédula"
						fullWidth
						margin="dense"
						value={cedulaOlvido}
						onChange={(e) => setCedulaOlvido(e.target.value)}
						inputProps={{ maxLength: 10, pattern: "[0-9]*" }}
						sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
					/>

					<Box sx={{ mt: 2, p: 2, backgroundColor: "rgba(45,54,137, 0.07)", borderRadius: "8px" }}>
						<Typography variant="body2" sx={{ color: "#2D3689", fontWeight: "500" }}>
							💡 Una vez verificados sus datos, recibirá instrucciones para restablecer su contraseña.
						</Typography>
					</Box>
				</DialogContent>

				<DialogActions sx={{ p: 3, pt: 1, gap: 2 }}>
					<Button
						onClick={() => {
							setShowOlvidoModal(false);
							setUsuarioOlvido("");
							setCedulaOlvido("");
						}}
						sx={{
							color: "#666",
							borderColor: "#666",
							"&:hover": { borderColor: "#333", color: "#333" }
						}}
						variant="outlined"
					>
						Cancelar
					</Button>
					<Button
						variant="contained"
						disabled={!usuarioOlvido.trim() || !cedulaOlvido.trim() || isLoadingOlvido}
						onClick={handleOlvidoPassword}
						sx={{
							backgroundColor: "#EB2A1B",
							color: "#fff",
							px: 4,
							borderRadius: "8px",
							fontWeight: "bold",
							"&:hover": { backgroundColor: "#c01f12" },
							"&:disabled": { backgroundColor: "#ccc", color: "#666" },
						}}
					>
						{isLoadingOlvido ? (
							<>
								<CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
								Procesando...
							</>
						) : (
							"Recuperar Contraseña"
						)}
					</Button>
				</DialogActions>
			</Dialog>

			{/* ====== Modal cambio de clave ====== */}
			<Dialog
				open={mostrarCambioClave}
				onClose={() => { }}
				disableEscapeKeyDown
				maxWidth="sm"
				fullWidth
				PaperProps={{
					sx: {
						borderRadius: "12px",
						background: "linear-gradient(135deg, #EB2A1B 0%, #2D3689 100%)",
						color: "white",
					},
				}}
			>
				<DialogTitle sx={{
					textAlign: "center",
					pb: 1,
					background: "rgba(255, 255, 255, 0.1)",
					backdropFilter: "blur(10px)",
				}}>
					<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
						<SecurityIcon sx={{ fontSize: "2rem", color: "#FFD700" }} />
						<Typography variant="h5" component="div" sx={{ fontWeight: "bold" }}>
							Configuración de Seguridad
						</Typography>
					</Box>
					<Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
						Al ser tu primer acceso, es necesario establecer una contraseña segura
					</Typography>
				</DialogTitle>

				<DialogContent sx={{
					p: 3,
					background: "rgba(255, 255, 255, 0.95)",
					color: "#333",
					m: 2,
					borderRadius: "8px",
				}}>
					<Alert
						severity="info"
						sx={{
							mb: 3,
							backgroundColor: "rgba(45,54,137, 0.08)",
							border: "1px solid rgba(45,54,137, 0.3)",
						}}
					>
						<Typography variant="body2" sx={{ fontWeight: "500" }}>
							🔒 Por tu seguridad, establece una contraseña que cumpla con los siguientes requisitos:
						</Typography>
					</Alert>

					<TextField
						label="Nueva contraseña"
						type={showNewPassword ? 'text' : 'password'}
						fullWidth
						margin="dense"
						value={nuevaClave}
						onChange={(e) => setNuevaClave(e.target.value)}
						InputProps={{
							endAdornment: (
								<InputAdornment position="end">
									<IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end">
										{showNewPassword ? <VisibilityOff /> : <Visibility />}
									</IconButton>
								</InputAdornment>
							),
						}}
						sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
					/>

					<TextField
						label="Confirmar contraseña"
						type={showConfirmPassword ? 'text' : 'password'}
						fullWidth
						margin="dense"
						value={confirmarClave}
						onChange={(e) => setConfirmarClave(e.target.value)}
						InputProps={{
							endAdornment: (
								<InputAdornment position="end">
									<IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
										{showConfirmPassword ? <VisibilityOff /> : <Visibility />}
									</IconButton>
								</InputAdornment>
							),
						}}
						sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
					/>

					{/* Indicadores de validación */}
					<Box sx={{ mt: 2, p: 2, backgroundColor: "rgba(0, 0, 0, 0.05)", borderRadius: "8px" }}>
						<Typography variant="body2" sx={{ fontWeight: "bold", mb: 1, color: "#555" }}>
							Requisitos de contraseña:
						</Typography>

						{[
							{ ok: passwordValidation.minLength, label: "Mínimo 8 caracteres y máximo 12 caracteres" },
							{ ok: passwordValidation.hasUpperCase, label: "Al menos una letra mayúscula (A-Z)" },
							{ ok: passwordValidation.hasLowerCase, label: "Al menos una letra minúscula (a-z)" },
							{ ok: passwordValidation.hasSpecialChar, label: "Al menos un carácter especial (!@#$%^&*...)" },
						].map(({ ok, label }) => (
							<Box key={label} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
								{ok ? (
									<CheckCircleIcon sx={{ color: "#2D3689", fontSize: "1rem" }} />
								) : (
									<CancelIcon sx={{ color: "#EB2A1B", fontSize: "1rem" }} />
								)}
								<Typography variant="body2" sx={{ color: ok ? "#2D3689" : "#EB2A1B" }}>
									{label}
								</Typography>
							</Box>
						))}

						{confirmarClave && (
							<Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
								{nuevaClave === confirmarClave ? (
									<CheckCircleIcon sx={{ color: "#2D3689", fontSize: "1rem" }} />
								) : (
									<CancelIcon sx={{ color: "#EB2A1B", fontSize: "1rem" }} />
								)}
								<Typography variant="body2" sx={{ color: nuevaClave === confirmarClave ? "#2D3689" : "#EB2A1B" }}>
									Las contraseñas coinciden
								</Typography>
							</Box>
						)}
					</Box>
				</DialogContent>

				<DialogActions sx={{ p: 3, pt: 1 }}>
					<Button
						variant="contained"
						fullWidth
						disabled={!passwordValidation.isValid || nuevaClave !== confirmarClave || !nuevaClave || !confirmarClave}
						onClick={async () => {
							if (nuevaClave !== confirmarClave) {
								enqueueSnackbar("Las contraseñas no coinciden", { variant: "error" });
								return;
							}
							if (!passwordValidation.isValid) {
								enqueueSnackbar("La contraseña no cumple con los requisitos de seguridad", { variant: "error" });
								return;
							}
							try {
								const ip = await obtenerIP();
								await axios.post(APIURL.cambiarClave(), {
									nombreUsuario: userName,
									nuevaClave,
									direccionIP: ip,
								});
								const token = localStorage.getItem("token");
								const expirationTime = new Date().getTime() + 3 * 60 * 60 * 1000;
								login(token, expirationTime);
								await conectarExtensionUsuario(usuarioExtensionId);
								enqueueSnackbar("Contraseña cambiada exitosamente", { variant: "success" });
								setMostrarCambioClave(false);
								navigate("/dashboard", { replace: true });
							} catch (err) {
								console.error("Error al cambiar clave o registrar ingreso:", err);
								enqueueSnackbar("Error al cambiar la contraseña", { variant: "error" });
							}
						}}
						sx={{
							backgroundColor: "#2D3689",
							color: "#fff",
							py: 1.5,
							borderRadius: "8px",
							fontSize: "1.1rem",
							fontWeight: "bold",
							"&:hover": { backgroundColor: "#1e2460" },
							"&:disabled": { backgroundColor: "#ccc", color: "#666" },
						}}
					>
						Establecer Nueva Contraseña Segura
					</Button>
				</DialogActions>
			</Dialog>
		</section>
	);
};

export default LoginMundial;
